<template>
    <v-dialog v-model="intDialogVisible" max-width="1100" scrollable content-class="score-detail-dialog">
        <v-card>
            <v-card-title class="text-h5">
                Скоринг клиента
                <v-icon v-if="score && score.hasContract" small color="blue" class="ml-2"
                        title="По этому номеру уже есть договор">mdi-file-document-check-outline</v-icon>
            </v-card-title>
            <v-card-text>
                <div v-if="notFound">
                    Клиент ещё не оценён.
                </div>
                <div v-else-if="score">
                    <div class="mb-2">
                        <v-chip dark :color="$helper.aiScoreColor(score.score)">
                            {{ score.score }}
                        </v-chip>
                        <span class="ml-2 grey--text" v-if="score.probability !== null && score.probability !== undefined">
                            вероятность {{ (score.probability * 100).toFixed(0) }}%
                        </span>
                    </div>

                    <div v-if="score.promptVersion === 'v1'" class="mb-2 grey--text text-caption">
                        <v-icon x-small color="grey">mdi-information-outline</v-icon>
                        Оценка перенесена со старой модели скоринга, будет пересчитана при следующем событии.
                    </div>

                    <div v-if="score.summary" class="mb-3">
                        <div class="text-caption grey--text">Резюме по клиенту</div>
                        <div>{{ score.summary }}</div>
                    </div>

                    <div v-if="score.recommendations && score.recommendations.length" class="mb-3">
                        <div class="text-caption grey--text">Рекомендации оператору</div>
                        <v-list dense>
                            <v-list-item v-for="(recommendation, i) in score.recommendations" :key="i" class="px-0">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small color="amber darken-2">mdi-lightbulb-on-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>{{ recommendation }}</v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>

                    <div v-if="score.reasoning" class="mb-2">
                        <div class="text-caption grey--text">Обоснование</div>
                        <div>{{ score.reasoning }}</div>
                    </div>

                    <div v-if="score.lastChangeReason" class="mb-2 grey--text">
                        <div class="text-caption">Что изменилось</div>
                        <div>{{ score.lastChangeReason }}</div>
                    </div>

                    <div v-if="hasChecklist" class="mb-3">
                        <div class="text-caption grey--text">Контроль оператора (последний звонок)</div>
                        <v-list dense>
                            <v-list-item v-for="entry in checklistEntries" :key="entry.label" class="px-0">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small :color="checklistColor(entry.item)">{{ checklistIcon(entry.item) }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <span>{{ entry.label }}</span>
                                    <span v-if="entry.item.comment" class="grey--text text-caption ml-1">— {{ entry.item.comment }}</span>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>

                    <v-chip-group v-if="score.signals && score.signals.length" column class="mb-2">
                        <v-chip v-for="(signal, i) in score.signals" :key="i" small outlined>
                            {{ signal }}
                        </v-chip>
                    </v-chip-group>

                    <v-expansion-panels flat class="mt-2" @change="loadHistory()">
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                История изменений
                                <span v-if="score.recomputeCount" class="grey--text text-caption ml-2">
                                    ({{ score.recomputeCount }})
                                </span>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-skeleton-loader v-if="historyLoading" type="list-item-two-line@3"></v-skeleton-loader>
                                <div v-else-if="!history.length" class="grey--text">История пуста.</div>
                                <v-expansion-panels v-else accordion flat>
                                    <v-expansion-panel v-for="item in history" :key="item.id">
                                        <v-expansion-panel-header class="px-0">
                                            <div>
                                                <span class="text-caption grey--text">{{ $moment(item.createdAt).dateTimeFormat() }}</span>
                                                <span class="ml-2">
                                                    <v-icon small :color="scoreDeltaColor(item)">{{ scoreDeltaIcon(item) }}</v-icon>
                                                    {{ item.previousScore === null ? '—' : item.previousScore }}
                                                    → {{ item.newScore === null ? '—' : item.newScore }}
                                                </span>
                                                <span class="ml-2 grey--text text-caption">{{ $helper.scoreTriggerLabel(item.trigger) }}</span>
                                            </div>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content class="px-0">
                                            <div v-if="item.changeReason" class="mb-1">{{ item.changeReason }}</div>
                                            <div v-if="item.summary" class="mb-1 grey--text">{{ item.summary }}</div>
                                            <ul v-if="item.recommendations && item.recommendations.length">
                                                <li v-for="(r, i) in item.recommendations" :key="i">{{ r }}</li>
                                            </ul>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-btn v-if="canRecompute" color="primary" text :loading="recomputing" @click="recompute()">
                    Пересчитать
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text @click="intDialogVisible = false">Закрыть</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import AiService from "../_services/AiService";
import {mapState} from 'vuex';

export default {
    name: "ScoreDetailDialog",
    props: {
        dialogVisible: {
            type: Boolean,
            default: false
        },
        requestId: {
            type: Number,
            default: null
        },
        phone: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            score: null,
            notFound: false,
            recomputing: false,
            history: [],
            historyLoaded: false,
            historyLoading: false
        }
    },
    computed: {
        ...mapState(['user']),
        intDialogVisible: {
            get() {
                return this.dialogVisible;
            },
            set(value) {
                if (!value) {
                    this.$emit('close');
                }
            }
        },
        canRecompute() {
            return this.$helper.isUserRole(this.user, 'ADMIN') || this.$helper.isUserRole(this.user, 'OPERATOR_MANAGER');
        },
        checklistEntries() {
            if (!this.score) {
                return [];
            }
            return [
                {label: 'Запись', item: this.score.checklistBooking},
                {label: 'Вопросы', item: this.score.checklistQuestions},
                {label: 'Возражения', item: this.score.checklistObjections},
                {label: 'Контакт', item: this.score.checklistContact}
            ].filter(entry => entry.item && entry.item.status);
        },
        hasChecklist() {
            return this.checklistEntries.length > 0;
        }
    },
    watch: {
        dialogVisible(visible) {
            if (visible) {
                this.fetch();
            }
        }
    },
    methods: {
        fetch() {
            this.historyLoaded = false;
            this.history = [];
            const request = this.phone ? AiService.getScoreByPhone(this.phone) : AiService.getRequestScore(this.requestId);
            request.then(data => {
                this.score = data;
                this.notFound = false;
            }, () => {
                this.score = null;
                this.notFound = true;
            });
        },
        loadHistory() {
            if (this.historyLoaded || !this.score || !this.score.cleanPhone) {
                return;
            }
            this.historyLoading = true;
            AiService.getScoreHistory(this.score.cleanPhone).then(data => {
                this.history = data;
                this.historyLoaded = true;
                this.historyLoading = false;
            }, () => {
                this.historyLoading = false;
            });
        },
        recompute() {
            const phone = this.score && this.score.cleanPhone ? this.score.cleanPhone : this.phone;
            this.recomputing = true;
            const request = phone ? AiService.recomputeScoreByPhone(phone) : AiService.recomputeScore(this.requestId);
            request.then(() => {
                this.recomputing = false;
                this.fetch();
                this.$emit('recomputed');
            }, error => {
                this.recomputing = false;
                this.flashError(this.$helper.showError(error));
            });
        },
        scoreDeltaIcon(item) {
            if (item.previousScore === null || item.newScore === null) {
                return 'mdi-arrow-right';
            }
            if (item.newScore > item.previousScore) {
                return 'mdi-arrow-up-bold';
            }
            if (item.newScore < item.previousScore) {
                return 'mdi-arrow-down-bold';
            }
            return 'mdi-arrow-right';
        },
        scoreDeltaColor(item) {
            if (item.previousScore === null || item.newScore === null) {
                return 'grey';
            }
            if (item.newScore > item.previousScore) {
                return 'green';
            }
            if (item.newScore < item.previousScore) {
                return 'red';
            }
            return 'grey';
        },
        checklistIcon(item) {
            if (item.status === 'PASS') {
                return 'mdi-check-circle';
            }
            if (item.status === 'FAIL') {
                return 'mdi-close-circle';
            }
            return 'mdi-minus-circle';
        },
        checklistColor(item) {
            if (item.status === 'PASS') {
                return 'green';
            }
            if (item.status === 'FAIL') {
                return 'red';
            }
            return 'grey';
        }
    }
}
</script>

<style lang="scss">
/* Unscoped on purpose: v-dialog content is teleported out of this component's root. */
.score-detail-dialog {
    .v-card__text {
        font-size: 1.1rem;
        line-height: 1.6;
    }

    .text-caption {
        font-size: 0.95rem !important;
        letter-spacing: normal !important;
    }

    .v-list--dense .v-list-item .v-list-item__content,
    .v-expansion-panel-header,
    .v-expansion-panel-content {
        font-size: 1.05rem;
        line-height: 1.55;
    }

    .v-chip:not(.v-size--small) {
        font-size: 1.25rem;
    }
}
</style>
